import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import useProgramTheme from '../../../../common/use-program-theme';
import {
  ModalRoot,
  ModalContent,
  ModalCancelButton,
  Icon,
  ModalTriggerButton,
  ModalPortal,
  ModalOverlay,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
  Button,
  Checkbox,
  Switch,
  ModalActionButton,
  TextArea,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  Tag,
  TagsContainer,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '@socialchorus/shared-ui-components';
import './notifications-card.scss';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { useAudiences } from '../../../../models/audiences/context';
import { Feature } from '../../../../models/features/features';
import useFeatureFlag from '../../../../common/use-feature-flag';
import { useDefaultChannelsQuery } from '../../../../common/queries/use-default-channels-query';

const NOTIFICATION_ERROR_ID = 'ugc-notification-error';
const LIMIT_POST_ERROR_ID = 'ugc-limit-post-error';

const MAX_CHARACTER_COUNT = 100;
const AUDIENCE_TAGS_SHOWN_INLINE = 7;

const CharacterCount: React.FC<{ content: string }> = ({ content }) => {
  return (
    <div className="ugc-notifications-card__character_count text-caption">
      {content.length}/{MAX_CHARACTER_COUNT}
    </div>
  );
};

const NotificationSummary: React.FC<{
  enabled: boolean;
  name: string;
  message: string;
  subMessage?: string;
  // Only channels with a dedicated preview field (currently email) should
  // render the submessage row at all — others have no such concept.
  hasPreviewField?: boolean;
  icon: string;
}> = ({ enabled, icon, name, message, subMessage, hasPreviewField }) => {
  const { t } = useTranslation();
  const noMessageText = t('user_generated_content.no_message_added');
  // Channels with a preview field (email) only warn when BOTH the message
  // and preview are blank — if either is set, show the real content with no
  // warning at all, even though the other field is empty.
  const bothFieldsEmpty = hasPreviewField && !message && !subMessage;
  return (
    <>
      {enabled && (
        <div className="ugc-notifications-card__summary-channel">
          <div className="ugc-notifications-card__summary-channel-name text-caption-bold">
            <Icon size={16}>{icon}</Icon>
            <div>{name}</div>
          </div>
          {bothFieldsEmpty ? (
            <div className="ugc-notifications-card__summary-channel-message text-body text-orange-600">
              {noMessageText}
            </div>
          ) : (
            <>
              <div
                className={cx(
                  'ugc-notifications-card__summary-channel-message',
                  'text-body',
                  { 'text-orange-600': !hasPreviewField && !message }
                )}
              >
                {message || (hasPreviewField ? '' : noMessageText)}
              </div>
              {hasPreviewField && subMessage && (
                <div className="ugc-notifications-card__summary-channel-submessage text-body text-black-60">
                  {subMessage}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

const NotificationCard = () => {
  const { t } = useTranslation();
  const { themeColor } = useProgramTheme();
  const {
    userGeneratedContent,
    updateUserGeneratedContentFormState,
    memberValidationErrors,
    notificationChannelOptional,
  } = useUserGeneratedContent();
  const { audiences: programAudiences } = useAudiences();
  // Keep the section visible when the saved value is on, even with no program
  // audiences — otherwise a flag set elsewhere can never be cleared in the form.
  const limitVisibilityChecked =
    !!userGeneratedContent.limit_visibility_to_audience;
  const showPostVisibility =
    programAudiences.length > 0 || limitVisibilityChecked;
  // "At least one notification channel required" (audience but no channel) and
  // "Notification details are required" (enabled channel with blank text) are
  // mutually exclusive, so the card shows at most one in the same slot.
  const notificationErrorKey = memberValidationErrors.notification
    ? 'user_generated_content.validation.notification_channel_required'
    : memberValidationErrors.notificationDetails
    ? 'user_generated_content.validation.notification_details_required'
    : null;
  const showNotificationError = !!notificationErrorKey;
  const showLimitPostError = memberValidationErrors.limitPostNeedsTargets;

  const notificationCenterEnabled = useFeatureFlag(
    Feature.NOTIFICATION_CENTER_ENABLED
  );

  const { data: channelDefaults } = useDefaultChannelsQuery();

  const notificationSettings = userGeneratedContent?.notification_settings;

  const canAddNotifications = useMemo(() => {
    return (
      userGeneratedContent.audiences &&
      userGeneratedContent.audiences.length > 0
    );
  }, [userGeneratedContent.audiences]);

  // When an audience is selected and the program has channel defaults
  // configured, seed notification_settings from them so the summary card with
  // an Edit button renders immediately (no need to open the modal first).
  const seededFromDefaults = useRef(false);
  useEffect(() => {
    if (!canAddNotifications) return;
    if (seededFromDefaults.current) return;
    if (notificationSettings) return;
    if (!channelDefaults) return;
    const anyDefault =
      channelDefaults.assistant ||
      channelDefaults.email ||
      channelDefaults.push;
    if (!anyDefault) return;
    seededFromDefaults.current = true;
    updateUserGeneratedContentFormState({
      notification_settings: {
        notification_center: { enabled: !!channelDefaults.assistant },
        email: { enabled: !!channelDefaults.email },
        push_notification: { enabled: !!channelDefaults.push },
      },
    });
  }, [
    canAddNotifications,
    channelDefaults,
    notificationSettings,
    updateUserGeneratedContentFormState,
  ]);

  const [modalOpen, setModalOpen] = useState(false);

  const [notificationsEnabled, setNotificationsEnabled] = useState(
    notificationSettings?.notification_center?.enabled
  );
  const [emailEnabled, setEmailEnabled] = useState(
    notificationSettings?.email?.enabled
  );
  const [pushEnabled, setPushEnabled] = useState(
    notificationSettings?.push_notification?.enabled
  );
  const [notificationCenterMessage, setNotificationCenterMessage] = useState(
    notificationSettings?.notification_center?.message
  );
  const [emailSubject, setEmailSubject] = useState(
    notificationSettings?.email?.subject
  );
  const [emailPreview, setEmailPreview] = useState(
    notificationSettings?.email?.preview
  );
  const [pushMessage, setPushMessage] = useState(
    notificationSettings?.push_notification?.message
  );

  // Touched flags for the three required fields. Set true only when Apply is
  // clicked with the field blank (see onApply below), and reset to false when
  // the modal re-opens. A field's error message only renders once it's been
  // touched, so validation surfaces on Apply only — not while typing/blurring.
  const [emailSubjectTouched, setEmailSubjectTouched] = useState(false);
  const [
    notificationCenterMessageTouched,
    setNotificationCenterMessageTouched,
  ] = useState(false);
  const [pushMessageTouched, setPushMessageTouched] = useState(false);

  // Seed the modal's local state every time it opens.
  // - If saved settings exist, use those.
  // - Else if program-level defaults are configured, seed channel toggles
  //   from them (messages start empty).
  // - Else all three channels default to ON.
  useEffect(() => {
    if (!modalOpen) return;
    const ns = notificationSettings;
    if (ns) {
      setNotificationsEnabled(!!ns.notification_center?.enabled);
      setEmailEnabled(!!ns.email?.enabled);
      // Push requires Notification Center when the NC feature is available;
      // otherwise the Push section would be hidden (see render guard below)
      // while Apply remains disabled awaiting pushMessage, stranding the modal.
      setPushEnabled(
        !!ns.push_notification?.enabled &&
          (!notificationCenterEnabled || !!ns.notification_center?.enabled)
      );
      setNotificationCenterMessage(ns.notification_center?.message);
      setEmailSubject(ns.email?.subject);
      setEmailPreview(ns.email?.preview);
      setPushMessage(ns.push_notification?.message);
      return;
    }
    // A program whose admin configured Campaign Settings with no notification
    // channels still returns a channelDefaults object — just with every flag
    // false. Treat that the same as "no defaults configured" and fall through
    // to all-on, per FE-31697 AC3 ("modal opens with all channels enabled by
    // default when no program default exists").
    const hasAnyDefault =
      !!channelDefaults &&
      (channelDefaults.assistant ||
        channelDefaults.email ||
        channelDefaults.push);
    if (hasAnyDefault) {
      setNotificationsEnabled(!!channelDefaults.assistant);
      setEmailEnabled(!!channelDefaults.email);
      setPushEnabled(!!channelDefaults.push);
    } else {
      setNotificationsEnabled(true);
      setEmailEnabled(true);
      setPushEnabled(true);
    }
    setNotificationCenterMessage(undefined);
    setEmailSubject(undefined);
    setEmailPreview(undefined);
    setPushMessage(undefined);
  }, [modalOpen, notificationSettings, channelDefaults]);

  // Reset touched flags synchronously in onModalOpenChange below so a reopened
  // modal never paints with stale errors from the previous session.
  const onModalOpenChange = useCallback((open: boolean) => {
    setModalOpen(open);
    if (open) {
      setEmailSubjectTouched(false);
      setNotificationCenterMessageTouched(false);
      setPushMessageTouched(false);
    }
  }, []);

  const showEmailSubjectError =
    !!emailEnabled && emailSubjectTouched && !emailSubject?.trim();
  const showNotificationCenterMessageError =
    !!notificationsEnabled &&
    notificationCenterMessageTouched &&
    !notificationCenterMessage?.trim();
  const showPushMessageError =
    !!pushEnabled && pushMessageTouched && !pushMessage?.trim();

  const hasNotifications =
    canAddNotifications &&
    (notificationSettings?.notification_center?.enabled ||
      notificationSettings?.email?.enabled ||
      notificationSettings?.push_notification?.enabled);

  const enabledChannelCount = [
    notificationSettings?.notification_center?.enabled,
    notificationSettings?.email?.enabled,
    notificationSettings?.push_notification?.enabled,
  ].filter(Boolean).length;

  const audienceCount = userGeneratedContent.audiences?.length ?? 0;

  const audienceTags = (userGeneratedContent.audiences ?? []).map(
    (audience) => <Tag key={audience.name} label={audience.title} />
  );
  const hasAudienceOverflow = audienceTags.length > AUDIENCE_TAGS_SHOWN_INLINE;
  const visibleAudienceTags = hasAudienceOverflow
    ? audienceTags.slice(0, AUDIENCE_TAGS_SHOWN_INLINE)
    : audienceTags;

  const summaryAriaLabel = t(
    'user_generated_content.notification_summary_aria_label',
    {
      count: enabledChannelCount,
      channels: t('user_generated_content.notification_summary_channel', {
        count: enabledChannelCount,
      }),
    }
  );

  // Turning Notification Center off forces Push off.
  const onToggleNotificationCenter = useCallback(() => {
    setNotificationsEnabled((prev) => {
      const next = !prev;
      if (!next) setPushEnabled(false);
      return next;
    });
  }, []);

  // Apply is enabled when every enabled channel has its required fields
  // filled. At least one channel must be enabled — an all-off Apply
  // would publish with no notifications attached, which is
  // indistinguishable from never having opened the modal. This is the
  // only condition that visually disables the Apply button; required-field
  // validation runs on click. Exception (FE-37566): when the post is focused
  // to a limited audience, notifications are optional, so unchecking every
  // channel is a valid, submittable state and must not block Apply.
  const hasAnyChannelEnabled =
    !!notificationsEnabled || !!emailEnabled || !!pushEnabled;

  const canApply = useMemo(() => {
    if (!canAddNotifications) return false;
    if (!notificationChannelOptional && !hasAnyChannelEnabled) return false;
    if (notificationsEnabled && !notificationCenterMessage?.trim())
      return false;
    if (emailEnabled && !emailSubject?.trim()) return false;
    if (pushEnabled && !pushMessage?.trim()) return false;
    return true;
  }, [
    canAddNotifications,
    notificationChannelOptional,
    hasAnyChannelEnabled,
    notificationsEnabled,
    emailEnabled,
    pushEnabled,
    notificationCenterMessage,
    emailSubject,
    pushMessage,
  ]);

  const onApply = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Surface required-field errors for every enabled channel whose
      // required field is blank — clicking Apply acts like a global "touch
      // everything" so the user sees what's missing without having to blur
      // each field one by one.
      if (emailEnabled && !emailSubject?.trim()) {
        setEmailSubjectTouched(true);
      }
      if (notificationsEnabled && !notificationCenterMessage?.trim()) {
        setNotificationCenterMessageTouched(true);
      }
      if (pushEnabled && !pushMessage?.trim()) {
        setPushMessageTouched(true);
      }

      if (!canApply) {
        // Keep the modal open on a validation-failed click; the modal
        // action would otherwise close it after onClick unless we call
        // preventDefault here.
        event.preventDefault();
        return;
      }
      updateUserGeneratedContentFormState({
        notification_settings: {
          notification_center: {
            enabled: notificationsEnabled ?? false,
            message: notificationCenterMessage,
          },
          email: {
            enabled: emailEnabled ?? false,
            subject: emailSubject,
            preview: emailPreview,
          },
          push_notification: {
            enabled: pushEnabled ?? false,
            message: pushMessage,
          },
        },
      });
    },
    [
      canApply,
      updateUserGeneratedContentFormState,
      notificationsEnabled,
      notificationCenterMessage,
      emailEnabled,
      emailSubject,
      emailPreview,
      pushEnabled,
      pushMessage,
    ]
  );

  const onLimitVisibilityChange = useCallback(
    (checked: boolean) => {
      updateUserGeneratedContentFormState({
        limit_visibility_to_audience: checked,
      });
    },
    [updateUserGeneratedContentFormState]
  );

  const emptyBody = (
    <div
      className={cx('ugc-notifications-card__empty_modal_trigger', {
        'ugc-notifications-card__empty_modal_trigger--disabled':
          !canAddNotifications,
      })}
    >
      <div className="ugc-notifications-card__icon_container">
        <Icon style={{ color: themeColor }}>notifications</Icon>
      </div>
      <div className="ugc-notifications-card__cta_text text-caption">
        {canAddNotifications
          ? t('user_generated_content.no_notifications')
          : t('user_generated_content.please_add_audiences')}
      </div>
      <Button
        leftIcon={<Icon>add</Icon>}
        variant="text"
        className="ugc-notifications-card__add_button"
        style={{ color: themeColor }}
        disabled={!canAddNotifications}
        label={t('user_generated_content.add_notification')}
      />
    </div>
  );

  // Hide the whole section when the user has no audience access at all — an
  // audience-less "Add Notification" ghost card has nothing to target. Keep
  // it visible if the post already has saved notification_settings (e.g.
  // audience access was revoked after the fact) so that value stays
  // reviewable/clearable, mirroring how `limitVisibilityChecked` above keeps
  // the post-visibility checkbox visible for the same reason.
  if (
    programAudiences.length === 0 &&
    !userGeneratedContent.notification_settings
  ) {
    return null;
  }

  return (
    <ModalRoot open={modalOpen} onOpenChange={onModalOpenChange}>
      <div className="ugc-notifications-card">
        {showPostVisibility && (
          <div className="ugc-post-visibility">
            <div className="ugc-post-visibility__label text-body">
              {t('user_generated_content.post_visibility')}
              <TooltipRoot>
                <TooltipTrigger
                  aria-label={t(
                    'user_generated_content.post_visibility_tooltip_aria'
                  )}
                >
                  <Icon size={16}>info</Icon>
                </TooltipTrigger>
                <TooltipContent
                  description={t(
                    'user_generated_content.post_visibility_tooltip'
                  )}
                  sideOffset={5}
                />
              </TooltipRoot>
            </div>
            <Checkbox
              label={t(
                'user_generated_content.limit_post_to_specified_audiences'
              )}
              checked={limitVisibilityChecked}
              onCheckedChange={(checked) =>
                onLimitVisibilityChange(checked === true)
              }
              aria-invalid={showLimitPostError || undefined}
              aria-describedby={
                showLimitPostError ? LIMIT_POST_ERROR_ID : undefined
              }
              aria-errormessage={
                showLimitPostError ? LIMIT_POST_ERROR_ID : undefined
              }
            />
            {showLimitPostError && (
              <div
                id={LIMIT_POST_ERROR_ID}
                role="alert"
                className="ugc-notifications-card__error"
              >
                <Icon size={16}>warning</Icon>
                <span className="text-caption">
                  {t(
                    'user_generated_content.validation.limit_post_needs_targets'
                  )}
                </span>
              </div>
            )}
          </div>
        )}
        <div
          className={
            hasNotifications
              ? 'ugc-notifications-card__label'
              : 'ugc-notifications-card__label_empty'
          }
        >
          {hasNotifications && (
            <div className="text-body">
              {t('user_generated_content.notifications')}
            </div>
          )}
        </div>
        <div
          className={
            hasNotifications
              ? 'ugc-notifications-card__body_transparent'
              : 'ugc-notifications-card__body'
          }
        >
          {hasNotifications && (
            <div className="ugc-notifications-card__summary">
              <div
                className="ugc-notifications-card__summary-heading text-body"
                aria-label={summaryAriaLabel}
              >
                <div aria-hidden="true">
                  <strong>{enabledChannelCount}</strong>
                  <span>
                    {' '}
                    {t('user_generated_content.notification_summary_channel', {
                      count: enabledChannelCount,
                    })}
                  </span>
                </div>
                <ModalTriggerButton
                  size="compact"
                  label={t('common.edit')}
                  variant="text"
                  style={{ color: themeColor }}
                  className="ugc-notifications-card__edit_button"
                  aria-invalid={showNotificationError || undefined}
                  aria-describedby={
                    showNotificationError ? NOTIFICATION_ERROR_ID : undefined
                  }
                  aria-errormessage={
                    showNotificationError ? NOTIFICATION_ERROR_ID : undefined
                  }
                />
              </div>

              <div className="ugc-notifications-card__summary-body">
                <span className="text-caption">
                  {t('user_generated_content.notification_summary_targeting')}{' '}
                  <span className="nowrap">
                    <PopoverRoot>
                      <PopoverTrigger
                        style={{ color: themeColor }}
                        className="ugc-notifications-card__summary-audience-count"
                      >
                        {t(
                          'user_generated_content.notification_summary_audience',
                          {
                            count: audienceCount,
                          }
                        )}
                      </PopoverTrigger>
                      <PopoverContent>
                        <TagsContainer
                          popoverTags={
                            hasAudienceOverflow ? audienceTags : undefined
                          }
                          visibleTags={visibleAudienceTags}
                        />
                      </PopoverContent>
                    </PopoverRoot>
                    {':'}
                  </span>
                </span>

                <NotificationSummary
                  enabled={notificationSettings?.email?.enabled || false}
                  icon="mail_outline"
                  name={t('user_generated_content.notification_summary_email')}
                  message={notificationSettings?.email?.subject || ''}
                  subMessage={notificationSettings?.email?.preview}
                  hasPreviewField
                />

                {notificationCenterEnabled && (
                  <NotificationSummary
                    enabled={
                      notificationSettings?.notification_center?.enabled ||
                      false
                    }
                    icon="notifications_none"
                    name={t(
                      'user_generated_content.notification_summary_notification_center'
                    )}
                    message={
                      notificationSettings?.notification_center?.message || ''
                    }
                  />
                )}

                <NotificationSummary
                  enabled={
                    notificationSettings?.push_notification?.enabled || false
                  }
                  icon="tablet_android"
                  name={t(
                    'user_generated_content.notification_summary_push_notification'
                  )}
                  message={
                    notificationSettings?.push_notification?.message || ''
                  }
                />
              </div>
            </div>
          )}
          {!hasNotifications &&
            // Only wrap the empty-state card in a ModalTriggerButton when
            // notifications can actually be added — otherwise clicks on the
            // div body would still open the modal, since `disabled` on the
            // trigger doesn't block bubbling through `asChild` content.
            (canAddNotifications ? (
              <ModalTriggerButton
                asChild
                aria-invalid={showNotificationError || undefined}
                aria-describedby={
                  showNotificationError ? NOTIFICATION_ERROR_ID : undefined
                }
                aria-errormessage={
                  showNotificationError ? NOTIFICATION_ERROR_ID : undefined
                }
              >
                {emptyBody}
              </ModalTriggerButton>
            ) : (
              emptyBody
            ))}
          <ModalPortal>
            <ModalOverlay />
            <ModalContent
              size="medium"
              className="ugc-notifications-card__content"
            >
              <ModalContentHeader
                title={t('user_generated_content.notification_settings')}
              />
              <ModalContentBody>
                <div className="ugc-notifications-card__item">
                  <Button
                    className={'ugc-notifications-card__toggle'}
                    variant="primary"
                    fullWidth
                    disabled={!canAddNotifications}
                    label={t('common.email')}
                    leftIcon={<Icon>mail</Icon>}
                    rightIcon={<Switch checked={emailEnabled} />}
                    onClick={() => setEmailEnabled((prev) => !prev)}
                  />
                  {emailEnabled && (
                    <>
                      <label
                        htmlFor="notifications-card__email-subject-input"
                        className="ugc-notifications-card__field_label text-body-bold"
                      >
                        {t('user_generated_content.email_subject_label')}
                      </label>
                      <TextArea
                        id="notifications-card__email-subject-input"
                        className="ugc-notifications-card__textarea"
                        value={emailSubject}
                        onChange={setEmailSubject}
                        maxLength={MAX_CHARACTER_COUNT}
                        disabled={!canAddNotifications}
                        placeholder={t(
                          'user_generated_content.email_subject_placeholder'
                        )}
                        aria-invalid={showEmailSubjectError || undefined}
                        aria-describedby={
                          showEmailSubjectError
                            ? 'ugc-notifications-card__email-subject-error'
                            : undefined
                        }
                        aria-errormessage={
                          showEmailSubjectError
                            ? 'ugc-notifications-card__email-subject-error'
                            : undefined
                        }
                      />
                      {showEmailSubjectError && (
                        <div
                          id="ugc-notifications-card__email-subject-error"
                          role="alert"
                          className="ugc__validation-error ugc-notifications-card__field_error"
                        >
                          <Icon>warning</Icon>
                          <span>
                            {t(
                              'user_generated_content.validation.email_subject_required'
                            )}
                          </span>
                        </div>
                      )}
                      <CharacterCount content={emailSubject || ''} />
                      <label
                        htmlFor="notifications-card__email-preview-input"
                        className="ugc-notifications-card__field_label text-body-bold"
                      >
                        {t('user_generated_content.email_preview_label')}
                      </label>
                      <TextArea
                        id="notifications-card__email-preview-input"
                        className="ugc-notifications-card__textarea"
                        value={emailPreview}
                        onChange={setEmailPreview}
                        maxLength={MAX_CHARACTER_COUNT}
                        disabled={!canAddNotifications}
                        placeholder={t(
                          'user_generated_content.email_preview_placeholder'
                        )}
                      />
                      <CharacterCount content={emailPreview || ''} />
                    </>
                  )}
                </div>
                {notificationCenterEnabled && (
                  <div className="ugc-notifications-card__item">
                    <Button
                      className={
                        'ugc-notifications-card__toggle ugc-notifications-card__notification-center-toggle'
                      }
                      variant="primary"
                      fullWidth
                      disabled={!canAddNotifications}
                      label={t('notification_center.title')}
                      leftIcon={<Icon>notifications</Icon>}
                      rightIcon={<Switch checked={notificationsEnabled} />}
                      onClick={onToggleNotificationCenter}
                    />
                    {notificationsEnabled && (
                      <>
                        <TextArea
                          className="ugc-notifications-card__textarea"
                          value={notificationCenterMessage}
                          onChange={setNotificationCenterMessage}
                          maxLength={MAX_CHARACTER_COUNT}
                          disabled={!canAddNotifications}
                          placeholder={t(
                            'user_generated_content.notification_center_message_placeholder'
                          )}
                          aria-invalid={
                            showNotificationCenterMessageError || undefined
                          }
                          aria-describedby={
                            showNotificationCenterMessageError
                              ? 'ugc-notifications-card__nc-message-error'
                              : undefined
                          }
                          aria-errormessage={
                            showNotificationCenterMessageError
                              ? 'ugc-notifications-card__nc-message-error'
                              : undefined
                          }
                        />
                        {showNotificationCenterMessageError && (
                          <div
                            id="ugc-notifications-card__nc-message-error"
                            role="alert"
                            className="ugc__validation-error ugc-notifications-card__field_error"
                          >
                            <Icon>warning</Icon>
                            <span>
                              {t(
                                'user_generated_content.validation.notification_center_message_required'
                              )}
                            </span>
                          </div>
                        )}
                        <CharacterCount
                          content={notificationCenterMessage || ''}
                        />
                      </>
                    )}
                  </div>
                )}
                {(!notificationCenterEnabled || notificationsEnabled) && (
                  <div
                    className={
                      notificationsEnabled
                        ? 'ugc-notifications-card__item ugc-notifications-card__child-item'
                        : 'ugc-notifications-card__item'
                    }
                  >
                    <Button
                      className={'ugc-notifications-card__toggle'}
                      variant="primary"
                      fullWidth
                      disabled={!canAddNotifications}
                      label={t('user_generated_content.push_notification')}
                      leftIcon={<Icon>tablet_android</Icon>}
                      rightIcon={<Switch checked={pushEnabled} />}
                      onClick={() => setPushEnabled((prev) => !prev)}
                    />
                    {pushEnabled && (
                      <>
                        <TextArea
                          className="ugc-notifications-card__textarea"
                          value={pushMessage}
                          onChange={setPushMessage}
                          maxLength={MAX_CHARACTER_COUNT}
                          disabled={!canAddNotifications}
                          placeholder={t(
                            'user_generated_content.push_message_placeholder'
                          )}
                          aria-invalid={showPushMessageError || undefined}
                          aria-describedby={
                            showPushMessageError
                              ? 'ugc-notifications-card__push-message-error'
                              : undefined
                          }
                          aria-errormessage={
                            showPushMessageError
                              ? 'ugc-notifications-card__push-message-error'
                              : undefined
                          }
                        />
                        {showPushMessageError && (
                          <div
                            id="ugc-notifications-card__push-message-error"
                            role="alert"
                            className="ugc__validation-error ugc-notifications-card__field_error"
                          >
                            <Icon>warning</Icon>
                            <span>
                              {t(
                                'user_generated_content.validation.push_message_required'
                              )}
                            </span>
                          </div>
                        )}
                        <CharacterCount content={pushMessage || ''} />
                      </>
                    )}
                  </div>
                )}
              </ModalContentBody>
              <ModalContentFooter className="ugc-notifications-card__footer">
                <ModalCancelButton label={t('common.back')} variant="text" />
                <ModalActionButton
                  onClick={onApply}
                  label={t('common.apply')}
                  variant="primary"
                  // Visually disabled only when no channel is enabled. With
                  // a channel on but required fields blank, the button stays
                  // clickable so the click can surface the validation
                  // errors (onApply marks touched and preventDefaults).
                  // FE-37566: when notifications are optional for this post,
                  // an all-off state is valid and must not disable Apply.
                  disabled={
                    !canAddNotifications ||
                    (!notificationChannelOptional && !hasAnyChannelEnabled)
                  }
                  style={{ backgroundColor: themeColor }}
                />
              </ModalContentFooter>
            </ModalContent>
          </ModalPortal>
        </div>
        {notificationErrorKey && (
          <div
            id={NOTIFICATION_ERROR_ID}
            role="alert"
            className="ugc-notifications-card__error"
          >
            <Icon size={16}>warning</Icon>
            <span className="text-caption">{t(notificationErrorKey)}</span>
          </div>
        )}
      </div>
    </ModalRoot>
  );
};

export default NotificationCard;
